'use client'

import { motion } from 'framer-motion'
import { Star, Trophy, Target, Zap, Crown } from 'lucide-react'
import Image from 'next/image'

import alfonso from '@/assets/alfonso.jpg'
import logo from '@/assets/logo-white-text.png'


const ambassadors = [
  {
    name: "Alfonso Lara",
    position: "QB - FS",
    team: "Mayas",
    stats: "#10",
    img: 'alfonso',
    icon: Crown,
    gradient: "from-yellow-400 to-orange-500"
  },
  {
    name: "Por Revelarse", 
    position: "?",
    team: "?",
    stats: "?",
    icon: Zap,
    gradient: "from-blue-400 to-purple-500"
  },
  {
    name: "Por Revelarse", 
    position: "?",
    team: "?",
    stats: "?",
    icon: Target,
    gradient: "from-green-400 to-teal-500"
  },
  {
    name: "Por Revelarse", 
    position: "?",
    team: "?",
    stats: "?",
    icon: Trophy,
    gradient: "from-red-400 to-pink-500"
  },
  {
    name: "Por Revelarse", 
    position: "?",
    team: "?",
    stats: "?",
    icon: Star,
    gradient: "from-purple-400 to-indigo-500"
  }
]

// SVG Silhouette Component
const AthleteSilhouette = ({ index }: { index: number }) => {
  const positions = [
    { image: alfonso, alt: "Quarterback silhouette" },
    { image: logo, alt: "Running back silhouette" },
    { image: logo, alt: "Wide receiver silhouette" },
    { image: logo, alt: "Linebacker silhouette" },
    { image: logo, alt: "Defensive back silhouette" }
  ]

  const currentPosition = positions[index] || positions[0]

  return (
    <div className="w-full h-full relative">
      <img
        src={currentPosition.image}
        alt={currentPosition.alt}
        className="w-full h-full object-contain"
      />
    </div>
  )
}

export default function AmbassadorsSection() {
  return (
    <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(
            45deg,
            transparent,
            transparent 10px,
            rgba(255, 196, 0, 0.1) 10px,
            rgba(255, 196, 0, 0.1) 20px
          )`
        }} />
      </div>

      {/* Floating Football Icons */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-yellow-400/20 text-2xl"
            style={{
              left: `${10 + (i * 8)}%`,
              top: `${20 + Math.sin(i) * 30}%`,
            }}
            animate={{
              y: [0, -20, 0],
              rotate: [0, 360],
              opacity: [0.1, 0.3, 0.1]
            }}
            transition={{
              duration: 8 + i * 0.5,
              repeat: Infinity,
              delay: i * 0.8,
              ease: "easeInOut"
            }}
          >
            🏈
          </motion.div>
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.h2 
            className="text-5xl md:text-6xl font-black text-white mb-6"
            style={{ fontWeight: 900 }}
          >
            NUESTROS <span className="text-yellow-400">EMBAJADORES</span>
          </motion.h2>
          <div className="h-1 w-32 bg-gradient-to-r from-yellow-400 to-orange-500 mx-auto mb-6" />
          <motion.p 
            className="text-xl text-gray-300 max-w-3xl mx-auto"
            style={{ fontWeight: 400 }}
          >
            Los atletas más destacados del flag football en el Caribe Mexicano
          </motion.p>
        </motion.div>

        {/* Ambassadors Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
          {ambassadors.map((ambassador, index) => {
            const IconComponent = ambassador.icon
            
            return (
              <motion.div
                key={ambassador.name}
                className="relative group"
                initial={{ opacity: 0, y: 100 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ 
                  duration: 0.2,
                }}
                whileHover={{ scale: 1.05 }}
              >
                {/* Card Background */}
                <div className="relative bg-gradient-to-b from-gray-800/50 to-gray-900/80 rounded-2xl p-6 backdrop-blur-sm border border-gray-700/50 hover:border-yellow-400/50 transition-all duration-500 h-full">
                  
                  {/* Athlete Silhouette */}
                  <div className="relative mb-6">
                    <div className={`h-64 w-full rounded-xl bg-gradient-to-br ${ambassador.gradient} p-1`}>
                      <div className="h-full w-full bg-black rounded-xl flex items-center justify-center overflow-hidden">
                        <div className="w-full">
                          <AthleteSilhouette index={index} />
                        </div>
                      </div>
                    </div>
                    
                    {/* Floating Icon */}
                    <motion.div
                      className={`absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br ${ambassador.gradient} rounded-full flex items-center justify-center shadow-2xl`}
                      animate={{
                        rotate: [0, 360],
                        scale: [1, 1.1, 1]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      <IconComponent className="w-6 h-6 text-black" />
                    </motion.div>
                  </div>

                  {/* Ambassador Info */}
                  <div className="space-y-3">
                    <h3 className="text-xl font-black text-white group-hover:text-yellow-400 transition-colors duration-300">
                      {ambassador.name}
                    </h3>
                    
                    <p className="text-yellow-400 font-semibold">
                      {ambassador.position}
                    </p>
                    
                    <p className="text-gray-300 text-sm">
                      {ambassador.team}
                    </p>
                    
                    <div className={`inline-block px-3 py-1 rounded-full bg-gradient-to-r ${ambassador.gradient} text-black text-sm font-bold`}>
                      {ambassador.stats}
                    </div>
                  </div>

                  {/* Hover Glow Effect */}
                  <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${ambassador.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500 pointer-events-none`} />
                </div>

                {/* Floating Elements Around Card */}
                <motion.div
                  className="absolute -z-10 inset-0"
                  animate={{
                    scale: [1, 1.05, 1],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    delay: index * 0.5,
                    ease: "easeInOut"
                  }}
                >
                  <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${ambassador.gradient} opacity-20 blur-xl`} />
                </motion.div>
              </motion.div>
            )}
          )}
        </div>

        {/* Call to Action */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <motion.button
            className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-12 py-4 rounded-full text-xl font-bold hover:shadow-2xl transition-all duration-300"
            whileHover={{ 
              scale: 1.05,
              boxShadow: '0 20px 40px rgba(255, 196, 0, 0.4)'
            }}
            whileTap={{ scale: 0.95 }}
            style={{ fontWeight: 700 }}
          >
            CONOCE A TODOS LOS EMBAJADORES
          </motion.button>
        </motion.div>
      </div>
    </section>
  )
}